import React from 'react';
import PropTypes from 'prop-types';
import ManagedInput from 'components/form/managed_input';

import classNames from 'classnames';
import isArray from 'lodash/isArray';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';
import startCase from 'lodash/startCase';

class HorizontalForm extends React.Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    errors: PropTypes.object
  };

  render() {
    const errors = this.props.errors || {};
    const children = React.Children.map(this.props.children, (child) => {
      if (!child) return child;

      const { name } = child.props;

      return React.cloneElement(child, {
        error: errors[name]
      });
    });

    return (
      <div className="form-vertical-field-container form-horizontal" style={{ '--bp-bg-opacity': '1' }}>
        {children}
      </div>
    );
  }
}

HorizontalForm.TextInput = class TextInput extends React.Component {
  static propTypes = {
    disabled: PropTypes.bool,
    error: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    number: PropTypes.bool,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    onChange: PropTypes.func.isRequired
  };

  label() {
    return this.props.label || startCase(this.props.name);
  }

  render() {
    return (
      <div className={`form-group ${this.props.required ? 'required' : ''} ${this.props.error ? 'has-error' : ''}`}>
        <div className="d-flex align-items-center">
          <label className='col-form-label'>
            {this.label()}
            {this.props.required && (
              <abbr title='required'>*</abbr>
            )}
          </label>
          <input
            className='form-control'
            disabled={this.props.disabled}
            name={this.props.name}
            placeholder={this.props.placeholder}
            required={this.props.required || false}
            type={this.props.number ? 'number' : 'text'}
            value={this.props.value || ''}
            onChange={this.props.onChange}
          />
        </div>
        {this.props.error &&
          <span className='form-text text-danger'>{this.props.error}</span>
        }
      </div>
    );
  }
};

HorizontalForm.Checkbox = class Checkbox extends React.Component {
  static propTypes = {
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    value: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired
  };

  label() {
    return this.props.label || startCase(this.props.name);
  }

  render() {
    return (
      <div className='form-group boolean'>
        <div className='checkbox form-check border rounded p-2'>
          <label className='boolean'>
            <input
              checked={this.props.value}
              className="boolean"
              name={this.props.name}
              type="checkbox"
              onChange={this.props.onChange}
            />
            {this.label()}
          </label>
        </div>
      </div>
    );
  }
};

HorizontalForm.RadioGroup = class RadioGroup extends React.Component {
  static propTypes = {
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired
    })).isRequired,
    required: PropTypes.bool,
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired
  }

  handleChange = (event) => {
    this.props.onChange({ [this.props.name]: event.currentTarget.firstElementChild.value });
  }

  render() {
    const { label, options, required } = this.props;

    return (
      <div className={classNames('form-group', { required })}>
        <label className="control-label">
          {this.props.label || startCase(this.props.name)}
        </label>
        <div className="form-input-container">
          {options.map(this.renderOption)}
        </div>
      </div>
    );
  }

  renderOption = ({ label, value }, index) => {
    return (
      <label
        className={classNames('radio-inline', { 'ml-2': index === 0 })}
        key={index}
        onClick={this.handleChange}
      >
        <input checked={this.props.value === value} readOnly type="radio" value={value} />
        {label}
      </label>
    );
  }
};

HorizontalForm.Select = class Select extends React.Component {
  static propTypes = {
    disabled: PropTypes.bool,
    includeBlank: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool
    ]),
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    noLabel: PropTypes.bool,
    options: PropTypes.array.isRequired,
    required: PropTypes.bool,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired
  };

  label() {
    return this.props.label || startCase(this.props.name);
  }

  renderOptions() {
    return this.props.options.map((option) => {
      if (isPlainObject(option)) {
        return <option key={option.value} value={option.value}>{option.label}</option>;
      } else if (isArray(option)) {
        return <option key={option[1]} value={option[1]}>{option[0]}</option>;
      } else {
        return <option key={option} value={option}>{option}</option>;
      }

    });
  }

  render() {
    const blankString = isString(this.props.includeBlank) ? this.props.includeBlank : '';

    return (
      <div className={`form-group select ${this.props.required ? 'required' : ''}`}>
        <div className="d-flex align-items-center">
          { !this.props.noLabel &&
            <label className='col-form-label select'>
              {this.label()}
              {this.props.required && (
                <abbr title='required'>*</abbr>
              )}
            </label>
          }
          <select
            className='form-select select'
            disabled={this.props.disabled}
            name={this.props.name}
            value={this.props.value}
            onChange={this.props.onChange}
          >
            {this.props.includeBlank && (
              <option value=''>{blankString}</option>
            )}
            {this.renderOptions()}
          </select>
        </div>
      </div>
    );
  }
};

HorizontalForm.TextArea = class TextArea extends React.Component {
  static propTypes = {
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired
  };

  label() {
    return this.props.label || startCase(this.props.name);
  }

  render() {
    return (
      <div className="form-group text">
        <div className="d-flex align-items-center">
          <label className="col-form-label text">{this.label()}</label>
          <textarea
            className="form-control text"
            name={this.props.name}
            value={this.props.value}
            onChange={this.props.onChange}
          />
        </div>
      </div>
    );
  }
};

HorizontalForm.DatePicker = class DateInput extends React.Component {
  static propTypes = {
    disabled: PropTypes.bool,
    error: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    required: PropTypes.bool,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object
    ]),
    onChange: PropTypes.func.isRequired
  };

  label() {
    return this.props.label || startCase(this.props.name);
  }

  render() {
    return (
      <div className={`form-group ${this.props.required ? 'required' : ''} ${this.props.error ? 'has-error' : ''}`}>
        <div className="d-flex align-items-center">
          <label className='col-form-label'>
            {this.label()}
            {this.props.required && (
              <abbr title='required'>*</abbr>
            )}
          </label>
          <ManagedInput
            className='form-control'
            disabled={this.props.disabled}
            name={this.props.name}
            required={this.props.required || false}
            type={'date'}
            value={this.props.value || ''}
            onChange={this.props.onChange}
          />
        </div>
        {this.props.error &&
          <span className='form-text text-danger'>{this.props.error}</span>
        }
      </div>
    );
  }
};

export default HorizontalForm;
